import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLandingEvents, type LandingEvent } from "@/hooks/useLandingEvents";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import { usePublishedExperiences } from "@/hooks/useExperiences";
import { matchesExperience } from "@/lib/events/matchExperience";
import { humanizeLandingDate } from "@/lib/events/humanizeLandingDate";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { pluralizeTillfalle } from "@/lib/text/pluralize";
import { resolveVenueText } from "@/lib/venue/resolveVenueText";
import { compareVenue } from "@/lib/venue/venueShadowCompare";
import { features } from "@/config/features";
import { useTranslate } from "@/i18n";
import ExperienceFilter from "./ExperienceFilter";

/**
 * Landing v2 — Life Signal
 * "Det händer här just nu" — calm, editorial event cards
 * with experience filtering, relative dates, and area context.
 */

const LABEL_MAP: Record<string, string> = {
  "nara-naturen": "Nära naturen",
  "i-rorelse": "I rörelse",
  "smak-av-garden": "Smak av gården",
};

export default function LifeSignalSection() {
  const { data: events, isLoading } = useLandingEvents(6);
  const { data: experiences } = usePublishedExperiences();
  const [selectedExperience, setSelectedExperience] = useState("all");
  const t = useTranslate();

  const experienceOptions = useMemo(() => {
    if (!experiences || !events) return [];
    return experiences
      .filter(e => LABEL_MAP[e.slug])
      .filter(e => events.some(ev => matchesExperience(ev.outing_tags, e.match_rules)))
      .map(e => ({ slug: e.slug, label: LABEL_MAP[e.slug] || e.title, matchRules: e.match_rules }));
  }, [experiences, events]);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    if (selectedExperience === "all") return events;
    const exp = experiences?.find(e => e.slug === selectedExperience);
    if (!exp) return events;
    return events.filter(ev => matchesExperience(ev.outing_tags, exp.match_rules));
  }, [events, selectedExperience, experiences]);

  if (!isLoading && (!events || events.length === 0)) return null;

  return (
    <section className="container max-w-3xl px-5 pt-16 pb-14 sm:pt-20 sm:pb-18">
      <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground tracking-[-0.015em] leading-[1.15]">
        {t("home.lifeSignal.title")}
      </h2>
      <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-lg">
        {t("home.lifeSignal.sub")}
      </p>

      {/* Experience filter */}
      {experienceOptions.length > 1 && (
        <div className="mt-6">
          <ExperienceFilter
            experiences={experienceOptions}
            selected={selectedExperience}
            onChange={setSelectedExperience}
          />
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => <EventCardSkeleton key={i} />)
          : filteredEvents.length === 0
            ? (
              <p className="col-span-full text-sm text-muted-foreground py-4">
                {t("home.lifeSignal.empty")}
              </p>
            )
            : filteredEvents.map((ev) => <LifeSignalCard key={ev.id} event={ev} />)
        }
      </div>

      {/* CTA */}
      <div className="mt-10 text-center">
        <Link
          to="/explore?mode=do"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent/80 transition-colors group"
        >
          {t("home.lifeSignal.ctaAll")}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </section>
  );
}

function LifeSignalCard({ event }: { event: LandingEvent }) {
  const coverUrl = useCoverImageUrl(event.cover_owner_type, event.cover_owner_id, 640);
  const isFleaMarket = event.kind === "flea_market";
  const t = useTranslate();

  const dateLabel = event.start_date
    ? format(new Date(event.start_date), "d MMM", { locale: sv })
    : null;

  const relativeLabel = humanizeLandingDate(event.start_date);

  // Legacy venue (before SoT)
  const legacyVenue = event.custom_address ?? event.producer_name;

  // SoT venue resolution (activities only)
  const { venue_text: sotVenue, venue_kind } = resolveVenueText({
    use_producer_address: event.use_producer_address,
    custom_address: event.custom_address,
    producer_name: event.producer_name,
  });

  // Shadow compare: log mismatches (activities only)
  if (!isFleaMarket) {
    compareVenue('landing', event.id, legacyVenue, sotVenue, venue_kind);
  }

  // Use SoT when flag is on, otherwise legacy. Flea market cards use place/producer labels directly.
  const venueText = isFleaMarket
    ? event.place_name ?? event.producer_name
    : features.useEventVenueSoT ? sotVenue : legacyVenue;

  // Compose date line: "Imorgon · 14 mar" or just "14 mar"
  const dateLine = relativeLabel && dateLabel
    ? `${relativeLabel} · ${dateLabel}`
    : relativeLabel || dateLabel;

  return (
    <Link
      to={event.href}
      className="group flex flex-col rounded-xl border border-border/40 bg-card overflow-hidden transition-all duration-200 hover:border-border hover:shadow-sm active:scale-[0.99]"
    >
      {/* Cover 16:9 */}
      <div className="aspect-[16/9] overflow-hidden bg-muted relative">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <Calendar className="h-10 w-10 text-muted-foreground/20" />
          </div>
        )}

        {isFleaMarket && (
          <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-background/90 text-[11px] font-medium text-foreground backdrop-blur-sm">
            {t("home.lifeSignal.fleaMarketBadge")}
          </span>
        )}

      </div>

      <div className="flex flex-col gap-1 p-4">
        {/* Title */}
        <h3 className="font-display text-base text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {event.title}
        </h3>

        {/* Venue — two-line: place then area */}
        {venueText && (
          <div className="flex flex-col gap-0.5">
            <div className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{venueText}</span>
            </div>
            {event.area_name && (
              <span className="text-xs text-muted-foreground/60 pl-4">
                i {event.area_name}
              </span>
            )}
          </div>
        )}

        {/* Date — with relative prefix */}
        {dateLine && (
          <span className="text-xs text-muted-foreground/60 mt-0.5">{dateLine}</span>
        )}

        {/* Course occurrence count */}
        {!isFleaMarket && event.event_type === 'course' && event.occurrences_count > 0 && (
          <span className="text-xs text-muted-foreground mt-0.5">
            {pluralizeTillfalle(event.occurrences_count)}
          </span>
        )}
      </div>
    </Link>
  );
}

function EventCardSkeleton() {
  return (
    <div className="rounded-xl border border-border/40 bg-card overflow-hidden">
      <Skeleton className="aspect-[16/9] w-full" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
  );
}
